import { useEffect, useRef, memo, useState } from 'react';

/**
 * ScientificFormula — Single MathJax 4 + mhchem + SVG renderer.
 *
 * Used in ALL rendering contexts: editor, preview, student view, correction, PDF.
 * Never displays raw TeX as fallback.
 *
 * Props:
 *   id       — stable formula ID (data-formula-id attribute)
 *   tex      — canonical TeX string (may contain \ce{} for chemistry)
 *   display  — "inline" | "block"
 *   onRenderStateChange(id, state) — callback when render succeeds/fails
 */
export const ScientificFormula = memo(function ScientificFormula({ id, tex, display = 'inline', onRenderStateChange }) {
  const ref = useRef(null);
  const [renderState, setRenderState] = useState('pending');

  useEffect(() => {
    if (!tex || !ref.current) return;

    const el = ref.current;
    const delim = display === 'block' ? ['$$', '$$'] : ['$', '$'];
    el.textContent = `${delim[0]}${tex}${delim[1]}`;
    el.setAttribute('data-render-state', 'pending');

    const doTypeset = () => {
      if (!window.MathJax?.typesetPromise) return false;
      window.MathJax.typesetPromise([el])
        .then(() => {
          // Verify SVG was produced
          const svg = el.querySelector('svg, mjx-container');
          const state = svg ? 'rendered' : 'error';
          setRenderState(state);
          el.setAttribute('data-render-state', state);
          onRenderStateChange?.(id, state);
        })
        .catch(() => {
          setRenderState('error');
          el.setAttribute('data-render-state', 'error');
          onRenderStateChange?.(id, 'error');
        });
      return true;
    };

    if (!doTypeset()) {
      const interval = setInterval(() => {
        if (doTypeset()) clearInterval(interval);
      }, 200);
      const timeout = setTimeout(() => {
        clearInterval(interval);
        if (el.getAttribute('data-render-state') === 'pending') {
          setRenderState('error');
          el.setAttribute('data-render-state', 'error');
          onRenderStateChange?.(id, 'error');
        }
      }, 5000);
      return () => { clearInterval(interval); clearTimeout(timeout); };
    }
  }, [tex, display, id, onRenderStateChange]);

  const isBlock = display === 'block';

  return (
    <span
      ref={ref}
      data-formula-id={id}
      data-render-state={renderState}
      className={isBlock ? 'block my-2 text-center' : 'inline-block align-middle'}
      style={renderState === 'error' ? { color: '#ef4444', fontSize: '0.75rem' } : undefined}
    />
  );
});

/**
 * RichContentRenderer — Renders a rich-science-v5 content AST.
 * Maps text nodes to <span> and formula nodes to <ScientificFormula>.
 */
export function RichContentRenderer({ content, onFormulaRenderState, className = '' }) {
  if (!content || !Array.isArray(content) || content.length === 0) {
    return <span className={className} />;
  }

  return (
    <span className={className}>
      {content.map((node, i) => {
        if (node.type === 'text') {
          return <span key={`t-${i}`}>{node.text}</span>;
        }
        if (node.type === 'formula') {
          return (
            <ScientificFormula
              key={node.id || `f-${i}`}
              id={node.id}
              tex={node.tex}
              display={node.display || 'inline'}
              onRenderStateChange={onFormulaRenderState}
            />
          );
        }
        if (node.type === 'image') {
          return (
            <img
              key={`img-${i}`}
              src={node.asset_id}
              alt={node.alt || 'Image scientifique'}
              className="inline-block max-h-16 align-middle"
            />
          );
        }
        return null;
      })}
    </span>
  );
}
